'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { EditorInstance } from '@nx-frontend/shared/types';
import { convertMarkdownToDelta } from '@nx-frontend/shared/utils';
import { Markdown } from './markdown';
import { TranscriptRemoveConfirm } from './transcript-confirm-remove';

export interface TranscriptSpeakerCardProps {
  index: number;
  role: string;
  content: string;
  inProgress: boolean;
  last: boolean;
  icon: {
    color: string;
    text?: number;
  };
  userShortName: string;
  onRemove: () => void;
}

export function TranscriptAiCard(props: TranscriptSpeakerCardProps) {
  const { index, role, content: contentRaw, icon, inProgress, userShortName, onRemove } = props;
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [sanitizedMarkup, setSanitizedMarkup] = useState<string>('');
  const { toast } = useToast();
  const t = useTranslations('features');

  const copyToBuffer = async () => {
    if (navigator.clipboard) {
      try {
        if (sanitizedMarkup) {
          const blob = new Blob([sanitizedMarkup], { type: 'text/html' });
          const item = new ClipboardItem({ 'text/html': blob });
          await navigator.clipboard.write([item]);
          toast({ title: t('transcript.copied') });
        }
      } catch (err) {
        console.info('Failed to copy: ', err);
        toast({ title: t('transcript.failed_to_copy'), description: t('transcript.unknown_error') });
      }
    } else {
      toast({ title: t('transcript.failed_to_copy'), description: t('transcript.clipboard_not_available') });
    }
  };

  const insertOnPage = async () => {
    const { text } = convertMarkdownToDelta(contentRaw);
    if (typeof window !== 'undefined' && 'gEditor' in window && window.gEditor) {
      const editor: EditorInstance = window.gEditor;
      editor.scroll.selection.setRange({ index: editor.scroll.length() - 1, length: 0 });
      editor.plugins.pasteManager.pasteNimbusOps(text as unknown as Record<string, unknown>);
    }
  };

  const handleDelete = () => {
    onRemove();
    setShowConfirmPopup(false);
  };

  const toggleConfirmPopup = () => {
    setShowConfirmPopup(!showConfirmPopup);
  };

  const setMarkup = (v: string) => {
    setSanitizedMarkup(v);
  };

  let content = null;
  if ('assistant' === role) {
    content = <Markdown setMarkup={setMarkup} markdownText={contentRaw} />;
  } else {
    content = contentRaw;
    if (content === 'IMPROVE') {
      content = t('transcript.suggest_improvements');
    } else if (content === 'SUMMARY') {
      content = t('transcript.make_summary');
    } else if (content === 'CTA') {
      content = t('transcript.make_call_to_actions');
    } else if (content === 'MEETING_NOTES') {
      content = t('transcript.make_meeting_notes');
    } else {
      const parts = content.split('<|>');
      if ('FOLLOW_UP' === parts[0]) {
        content = t('transcript.write_follow_up_email', { speaker: parts[2] });
      }
    }
  }

  return (
    <div
      data-ref={`transcript-speaker-card-${index}`}
      className={classNames(
        'text-[13px] mb-[8px] pl-[12px] pr-[15px] pt-[8px] pb-[8px] border border-transparent rounded-[6px] transition-bg duration-250',
        inProgress ? 'bg-[#F0FDFA]' : 'bg-transparent',
        'assistant' === role ? 'group' : ''
      )}
    >
      <div className="flex flex-row items-center mb-[8px] font-medium">
        <div className="flex grow items-center text-black">
          <div
            className={classNames(
              'rounded-full w-[20px] h-[20px]  flex items-center justify-center mr-[8px]',
              `bg-${icon.color}-50`
            )}
          >
            {'assistant' === role ? (
              <Icon icon={IconsMap.MAGIC_2} className="w-[20px] h-[20px] text-white" />
            ) : (
              <div className="w-[20px] h-[20px] text-white text-[10px] flex justify-center items-center gap-2 w-5 h-5 p-1.5 rounded-full bg-[var(--Light-theme-Success-Icon,#22C55E)]">
                {userShortName}
              </div>
            )}
          </div>
          <div className="flex h-[20px] items-center relative">
            <div>{'assistant' === role ? 'FuseBase AI' : 'You'}</div>
          </div>
        </div>
      </div>
      <div className="leading-[18.46px]">{content}</div>
      {'assistant' === role && !inProgress ? (
        <div className="flex items-center justify-start border-t border-[#E5E5E5] mt-[8px] pt-[8px] transition-opacity opacity-0 duration-250 group-hover:opacity-100">
          <button className="ml-[0px]" onClick={copyToBuffer}>
            <Icon icon={IconsMap.COPY} className="text-neutral-500 w-[16px] h-[16px] hover:text-cyan-900" />
          </button>
          <button className="ml-[8px]" onClick={insertOnPage}>
            <Icon icon={IconsMap.NOTE_PLUS} className="text-neutral-500 w-[16px] h-[16px] hover:text-cyan-900" />
          </button>
          <button className="ml-[8px]" onClick={toggleConfirmPopup}>
            <Icon icon={IconsMap.TRASH} className="text-neutral-500 w-[16px] h-[16px] hover:text-cyan-900" />
          </button>
        </div>
      ) : null}
      <div className="relative">
        <TranscriptRemoveConfirm
          isVisible={showConfirmPopup}
          title={t('transcript.delete_message_confirmation')}
          onConfirm={handleDelete}
          onCancel={() => setShowConfirmPopup(false)}
        />
      </div>
    </div>
  );
}
